// composables/usePoolaDeal.js
//
// Data layer + state machine for the member Poola buy screen. Owns the ONE
// current run (GET /api/v1/poola-deals/current) and derives a single `state`
// the host renders one child against. Read the real contract — don't guess:
//   ../backend/_bmad-output/planning-artifacts/poola-pwa-fe-contract-2026-06-30.md Section 2,Section 7
//   ../backend/app/Http/Controllers/Api/V1/Poola/PoolaDealController.php (current())
//
// The singleton returns ONE poola-deals resource (status ∈ {SCHEDULED, ACTIVE,
// ENDED}) OR { data: null } at HTTP 200 (no run, or an ineligible/pending
// member). `data === null` → the empty state; never a 404.
//
// ── Clock-skew guard ─────────────────────────────────────────────────────────
// The live window is money-sensitive: a skewed phone clock must not flip the
// run early/late. On the first successful fetch we capture a server-time offset
// from the response `Date` header (serverOffset = serverNow − clientNow) and
// expose OFFSET-ADJUSTED target timestamps. The display countdowns the run
// header builds with useCountdown(target) measure target − Date.now(); feeding
// them `targetMs − serverOffset` makes them measure against server time instead
// — algebraically `now = Date.now() + offset`. We do NOT rewrite useCountdown.
//
// ── Elapse → flip ────────────────────────────────────────────────────────────
// useCountdown captures a FIXED target at its synchronous call site and can't
// bind to the async-loaded startAt/endAt, so the elapse→refetch trigger lives
// here as a single boundary timer (no per-second interval): when the active
// phase's boundary elapses (coming→startAt opens the claim; live→endAt closes
// it) we silently refetch the singleton (status flips) and signal an offers
// refetch (canClaim flips), then re-derive `state`. The visible day/hour/min/sec
// countdowns belong to the run header (P5), fed by startAtAdjusted/endAtAdjusted.
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { usePoolaRealtime } from '@/composables/usePoolaRealtime.js';
import { POOLA_DEAL_STATUS } from '@/constants/poola.js';
import { jsonApiClient } from '@/httpConfig/config/axios.js';

export const CURRENT_DEAL_ENDPOINT = '/poola-deals/current';

// Bounded re-check poll for GENUINE promotion-cron lag: a FUTURE boundary we armed
// elapses under active watch, we refetch, and the server still reports the old
// status. We then re-fetch on a fixed interval up to a hard cap, then stop and rely
// on PTR / visibility-resume. This NEVER runs for a boundary that was already past
// at load — there `status` is authoritative (see armFlipTimer), which is what keeps
// a pre-dated / un-promoted SCHEDULED run from tight-looping the singleton.
const FLIP_RECHECK_MS = 5000;
const MAX_FLIP_RECHECKS = 6;

/** The one derived screen state. */
export const POOLA_STATE = {
  LOADING: 'loading',
  ERROR: 'error',
  EMPTY: 'empty',
  COMING: 'coming',
  LIVE: 'live',
  ENDED: 'ended',
};

/** ISO string | epoch-ms | null → epoch ms | null (NaN-safe). */
function toMs(value) {
  if (value == null) return null;
  const ms = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

// The recap card shows at most this many completed offers (the BE sends 0 to 6).
const PREVIOUS_RUN_HIGHLIGHTS_MAX = 6;

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * meta.previousRun → a safe shape | null. The BE adds the key only while the
 * CURRENT run is SCHEDULED (the last ENDED run of the club, so the countdown
 * screen can keep it visible). Absent, null, or anything malformed is "no
 * previous run" — the screen stays exactly what it is today. Ids arrive as
 * strings; keep them strings (compare as strings).
 */
function normalisePreviousRun(raw) {
  if (!isPlainObject(raw)) return null;
  if (raw.id == null || String(raw.id).trim() === '') return null;
  return {
    ...raw,
    id: String(raw.id),
    festival: isPlainObject(raw.festival) ? raw.festival : null,
    highlights: Array.isArray(raw.highlights)
      ? raw.highlights.filter(isPlainObject).slice(0, PREVIOUS_RUN_HIGHLIGHTS_MAX)
      : [],
  };
}

/**
 * usePoolaDeal — reactive run + state machine for PoolaPage.
 *
 * MUST be called from a component setup() — it registers onMounted/onUnmounted
 * to auto-fetch, drive the boundary timer, and clean up on teardown.
 *
 * @param {{ onFlip?: () => void }} [options]
 *   onFlip — invoked after an elapse-driven silent refetch completes. The host
 *   wires this to the offers refetch (e.g. `() => offers.refresh()`) so the
 *   data layer stays decoupled from usePoolaOffers (no cross-composable import).
 */
export function usePoolaDeal({ onFlip } = {}) {
  // The deserialised poola-deals resource (attributes flattened + id/type), or
  // null for the empty state.
  const deal = ref(null);
  const loading = ref(false); // true only during a NON-silent (first/PTR) fetch
  const error = ref(null);
  const hasLoaded = ref(false); // first successful fetch landed (real vs loading)

  // serverOffset = serverNow − clientNow (ms). Captured once from the `Date`
  // header on the first successful fetch; 0 until then (assume in-sync).
  const serverOffset = ref(0);
  let offsetCaptured = false;

  // Viewer identity from meta on each successful fetch (supplier read-only path).
  const viewerRole = ref(null); // 'supplier' | 'seller' | 'none' | null
  const readOnly = ref(false);

  // meta.festival aggregates (collective run/today totals, this member's season
  // savings, day index/count) from each successful fetch — null until the BE
  // ships them or when there is no current run. Every value is a REAL backend
  // counter; the festival surfaces feature-detect each field and hide when absent
  // (they never fabricate a number). See PoolaRun's festival slots.
  const festival = ref(null);

  // meta.previousRun — the last finished run of the club, carried by the backend
  // only while the CURRENT run is SCHEDULED (poola-last-run). Read-only data for
  // the recap card: it never feeds the state machine, the flip timer or the
  // offsets. null = absent / malformed / the key was dropped by a later fetch.
  const previousRun = ref(null);

  // The id of the run we OBSERVED flip SCHEDULED→ACTIVE during THIS page session
  // (null = no such transition seen). Drives the one-time festival "opening"
  // reveal — deal-scoped so a member who lands on an already-ACTIVE run, or a
  // later silent refetch that still returns ACTIVE, never re-fires it, while a
  // *different* run that opens later still fires exactly once (see fetchDeal).
  const openedDealId = ref(null);

  let flipTimer = null;
  let flipping = false;
  let recheckCount = 0; // bounded re-check budget once an armed boundary elapses unflipped
  let armedBoundaryMs = null; // the FUTURE boundary we armed a one-shot for (null = none)

  function clearFlipTimer() {
    if (flipTimer !== null) {
      clearTimeout(flipTimer);
      flipTimer = null;
    }
  }

  // ── Derived state ────────────────────────────────────────────────────────
  const state = computed(() => {
    if (!hasLoaded.value) {
      // Block on error only BEFORE the first success; a later silent refetch
      // failure keeps the last good state visible.
      return error.value ? POOLA_STATE.ERROR : POOLA_STATE.LOADING;
    }
    if (!deal.value) return POOLA_STATE.EMPTY;
    // Number()-coerce defensively: the constants are numeric, so a string
    // status from any future contract drift would otherwise fall through to
    // EMPTY and silently hide a live run.
    switch (Number(deal.value.status)) {
      case POOLA_DEAL_STATUS.SCHEDULED:
        return POOLA_STATE.COMING;
      case POOLA_DEAL_STATUS.ACTIVE:
        return POOLA_STATE.LIVE;
      case POOLA_DEAL_STATUS.ENDED:
        return POOLA_STATE.ENDED;
      default:
        // DRAFT/CANCELLED never reach the member surface; treat defensively.
        return POOLA_STATE.EMPTY;
    }
  });

  // ── Offset-adjusted display targets (for the run header's useCountdown) ────
  const startAtAdjusted = computed(() => {
    const ms = toMs(deal.value?.startAt);
    return ms == null ? null : ms - serverOffset.value;
  });
  const endAtAdjusted = computed(() => {
    const ms = toMs(deal.value?.endAt);
    return ms == null ? null : ms - serverOffset.value;
  });
  // Offset-adjusted target for the per-offer cap-unlock countdown. Built exactly
  // like startAt/endAtAdjusted so a skewed device clock can't fire the timer
  // early/late. null when no cap-increase is scheduled (capIncreaseAt absent).
  // NOTE: the cap lift NEVER changes deal.status — it is deliberately kept OUT of
  // the boundary flip timer / state machine (armFlipTimer). Consumers run their
  // own countdown to this and, on elapse, refetch offers (usePoolaOffers
  // .refetchForCapIncrease) — there is no status flip and no push.
  const capIncreaseAtAdjusted = computed(() => {
    const ms = toMs(deal.value?.capIncreaseAt);
    return ms == null ? null : ms - serverOffset.value;
  });

  /** Server-corrected "now" (ms) — for affordability/debug. */
  function serverNow() {
    return Date.now() + serverOffset.value;
  }

  // ── Boundary timer ────────────────────────────────────────────────────────
  // The adjusted boundary that, on elapse, flips the CURRENT phase.
  function nextBoundaryAdjusted() {
    if (state.value === POOLA_STATE.COMING) return startAtAdjusted.value;
    if (state.value === POOLA_STATE.LIVE) return endAtAdjusted.value;
    return null;
  }

  // Schedule a bounded, fixed-interval re-check after an armed FUTURE boundary
  // elapsed without the server transitioning (genuine promotion-cron lag) or a
  // flip-refetch failed. On budget exhaustion we drop the armed boundary so a
  // later visibility-resume can't restart the poll against the same stale anchor.
  function scheduleRecheck() {
    if (recheckCount >= MAX_FLIP_RECHECKS) {
      clearFlipTimer();
      armedBoundaryMs = null; // gave up on this boundary — PTR recovers from here
      return;
    }
    recheckCount += 1;
    flipTimer = setTimeout(flip, FLIP_RECHECK_MS);
  }

  function armFlipTimer() {
    clearFlipTimer();
    const boundary = nextBoundaryAdjusted();
    if (boundary == null) {
      armedBoundaryMs = null;
      return; // no bound to count to (e.g. null startAt/endAt)
    }
    const delay = boundary - Date.now();
    if (delay <= 0) {
      // Boundary ALREADY past at arm time. The backend `status` is authoritative
      // and has not advanced — this is a SCHEDULED run whose window opened but
      // whose status the backend keeps SCHEDULED (a pre-dated / un-promoted /
      // test run), NOT a few-seconds promotion lag. Do NOT poll: a past-boundary
      // refetch returns the same status and re-arms → a refetch loop (the bug).
      // Trust the status; PTR or a genuine FUTURE boundary picks up any change.
      armedBoundaryMs = null;
      return;
    }
    // A clean FUTURE boundary — arm a one-shot to flip exactly when it elapses.
    armedBoundaryMs = boundary;
    recheckCount = 0; // fresh re-check budget for this boundary
    flipTimer = setTimeout(flip, delay);
  }

  async function flip() {
    if (flipping) return;
    flipping = true;
    clearFlipTimer();
    const prevStatus = deal.value ? Number(deal.value.status) : null;
    try {
      const ok = await fetchDeal({ silent: true }); // re-arms a FUTURE boundary on success
      if (!ok) {
        // Transient refetch failure at the boundary — retry within budget so the
        // screen isn't stranded in the old phase until a manual refresh.
        scheduleRecheck();
        return;
      }
      const newStatus = deal.value ? Number(deal.value.status) : null;
      if (newStatus !== prevStatus) {
        // Real transition — fetchDeal already re-armed for the next boundary.
        if (typeof onFlip === 'function') onFlip();
      } else {
        // The armed boundary elapsed under active watch but the backend status
        // hasn't advanced yet (genuine promotion lag). Bounded re-check to catch
        // it soon, then stop. (fetchDeal's re-arm no-ops for the now-past anchor.)
        scheduleRecheck();
      }
    } finally {
      flipping = false;
    }
  }

  // ── Fetch ─────────────────────────────────────────────────────────────────
  // Returns true on success, false on a caught error — flip() uses this to
  // decide whether to retry.
  async function fetchDeal({ silent = false } = {}) {
    if (!silent) loading.value = true;
    // Captured BEFORE we overwrite deal.value so we can detect a status
    // transition. null on the first load (deal.value starts null), which is what
    // keeps a direct landing on an ACTIVE run from arming the opening reveal.
    const prevStatus = deal.value ? Number(deal.value.status) : null;
    try {
      const res = await jsonApiClient.get(CURRENT_DEAL_ENDPOINT);
      // jsonApiClient deserialises to { data, meta, links } (see axios.js);
      // `data` is the single resource or null.
      deal.value = res?.data?.data ?? null;

      const meta = res?.data?.meta;
      viewerRole.value = meta?.viewerRole ?? null;
      readOnly.value = meta?.readOnly ?? false;
      festival.value = meta?.festival ?? null;
      // Re-read on every successful fetch so a later one that no longer carries
      // the key (the next run opened) clears the card instead of keeping it.
      previousRun.value = normalisePreviousRun(meta?.previousRun);

      // Opening-reveal signal — armed ONCE per run, and ONLY on a genuinely
      // observed SCHEDULED→ACTIVE flip. prevStatus is null on first load, so
      // landing on an already-ACTIVE run does NOT arm it (the community-open
      // moment already passed). Deal-scoped, so a later silent refetch that still
      // returns ACTIVE can't re-fire it. Confetti is celebrated for THIS
      // community open only — never for a member's own claim.
      const newStatus = deal.value ? Number(deal.value.status) : null;
      if (prevStatus === POOLA_DEAL_STATUS.SCHEDULED && newStatus === POOLA_DEAL_STATUS.ACTIVE) {
        openedDealId.value = deal.value?.id ?? null;
      }

      if (!offsetCaptured) {
        const serverMs = toMs(res?.headers?.date);
        // Only mark captured once we actually parsed a usable Date — a missing
        // or garbage header on the FIRST response must not lock the skew guard
        // off for the session; a later response's good header still wins.
        if (serverMs != null) {
          serverOffset.value = serverMs - Date.now();
          offsetCaptured = true;
        }
      }

      error.value = null;
      hasLoaded.value = true;
      armFlipTimer();
      return true;
    } catch (err) {
      error.value = err;
      // Only blank the screen on a first-load failure; a later silent refetch
      // leaves the last good run on screen.
      if (!hasLoaded.value) deal.value = null;
      // eslint-disable-next-line no-console
      console.error('[usePoolaDeal] fetch error:', err);
      return false;
    } finally {
      if (!silent) loading.value = false;
    }
  }

  /** Full refetch (mount, pull-to-refresh, after a 409 deal-not-active claim). */
  async function refresh(done) {
    try {
      await fetchDeal({ silent: false });
    } finally {
      if (typeof done === 'function') done();
    }
  }

  // A silent refetch of the whole singleton, for the live-run host to pull fresh
  // meta.festival aggregates when a `.PoolaDealTotalsChanged` frame lands (the
  // coalesced collective-totals signal, forwarded up from PoolaRun). The event
  // carries only the run CASH total, but the thermometer shows the collective
  // PoolShare season total and the savings surface shows THIS viewer's season
  // savings — neither rides the event — so a refetch is the faithful way to move
  // them live. An in-flight guard
  // collapses a burst (the BE already coalesces to ~3s) to one request at a time.
  let festivalRefreshing = false;
  async function refreshFestival() {
    if (festivalRefreshing) return;
    festivalRefreshing = true;
    try {
      await fetchDeal({ silent: true });
    } finally {
      festivalRefreshing = false;
    }
  }

  // ── Realtime (progressive enhancement) ─────────────────────────────────────
  // Subscribe to the run's shared channel for the STATUS frame — the launch-
  // gating SCHEDULED→ACTIVE open (and the ACTIVE→ENDED close). We reuse flip(),
  // the exact boundary-timer path: a silent refetch that flips `status`, arms the
  // once-per-run opening reveal (openedDealId in fetchDeal), and drives the
  // offers refetch via PoolaPage's state-key remount. The boundary timer + PTR
  // stay as the no-socket fallback (tunnel mode has no socket) — NOT removed. The
  // channel is deal-scoped, so the deal_id filter is belt-and-suspenders.
  const dealId = computed(() => deal.value?.id ?? null);

  function onStatusEvent(event) {
    const id = deal.value?.id;
    if (id == null) return;
    if (event?.deal_id != null && String(event.deal_id) !== String(id)) return;
    // Already at the broadcast status (a duplicate frame, or the boundary timer
    // beat the socket to it) → nothing to do; skip the redundant recheck-poll a
    // no-change flip() would otherwise schedule.
    const incoming = Number(event?.status);
    if (Number.isFinite(incoming) && incoming === Number(deal.value?.status)) return;
    // Trust the server: flip() silently refetches + reconciles. No-op if a
    // boundary flip is already mid-flight (its `flipping` guard).
    flip();
  }

  usePoolaRealtime(dealId, {
    onStatus: onStatusEvent,
    // Gap-fill: a socket reconnect may have missed status/totals frames while down.
    onReconnect: () => fetchDeal({ silent: true }),
  });

  // The device may have slept past a boundary while hidden (a backgrounded
  // timer can be throttled away). On resume, catch up once if elapsed, else
  // re-arm the timer.
  function onVisibilityChange() {
    if (document.hidden) return;
    // Catch up ONLY if we were actively counting to a real FUTURE boundary that
    // elapsed while the tab was hidden (a backgrounded timer gets throttled away).
    // A boundary already past at load (armedBoundaryMs === null) must NOT flip here
    // — that would restart the very poll loop armFlipTimer avoids. Otherwise just
    // re-arm a still-future boundary (a no-op for an already-past one).
    if (armedBoundaryMs != null && armedBoundaryMs - Date.now() <= 0) {
      recheckCount = 0; // a genuinely elapsed armed boundary earns a fresh budget
      flip();
    } else {
      armFlipTimer();
    }
  }

  onMounted(() => {
    fetchDeal({ silent: false });
    document.addEventListener('visibilitychange', onVisibilityChange);
  });

  onUnmounted(() => {
    clearFlipTimer();
    document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  return {
    deal,
    state,
    loading,
    error,
    refresh,
    refreshFestival, // silent festival refetch on a `.PoolaDealTotalsChanged` signal
    serverOffset,
    serverNow,
    startAtAdjusted,
    endAtAdjusted,
    capIncreaseAtAdjusted, // offset-adjusted cap-unlock countdown target (null = no bump scheduled)
    viewerRole,
    readOnly,
    festival,
    previousRun, // last finished run (recap card) — null unless the BE sent it on a SCHEDULED current run
    openedDealId,
  };
}
